// The chat composer: a small rich-text editor (contentEditable) with a formatting toolbar, a link box, an
// emoji grid and the files waiting to go. Enter sends and Shift+Enter starts a new line (on the empty last line
// of a list or a quote, it steps out of it); on a touch keyboard, which has no Shift, Enter is a new line and
// the Send button sends. The box empties as a message goes, so typing on starts the next one. Pasted text keeps
// its formatting, cleaned to what a chat message can hold (cells from a spreadsheet as a line per row); a
// pasted image or screenshot becomes an attachment. On a phone the toolbar folds behind "Aa", so the composer
// stays one line until it's needed. What's typed stays with its thread while another one is open (for this
// visit), and is put aside while one of your messages is being edited.
import { useEffect, useLayoutEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent, type MutableRefObject, type ReactNode } from 'react';
import { chatHtmlToText, formatFileSize, sanitizeChatHtml, trimChatHtml } from '@technohull/shared';
import { pastedStylesToTags } from '../lib/richtext.js';
import { useIsCoarse, useIsPhone } from '../lib/media.js';
import {
  IconCheck, IconClearFormat, IconClose, IconCode, IconLink, IconList, IconListOrdered, IconPaperclip, IconPencil, IconQuote, IconSend, IconSmile,
} from '../components/icons.js';
import { EmojiPicker, rememberEmoji } from './EmojiPicker.js';
import { filesOf, type ChatUploads, type PendingUpload } from './chatUploads.js';
import { fileBadge, normalizeLinkInput, shortcutFor, type FormatCommand } from './chatFormat.js';

const drafts = new Map<string, string>();

const exec = (cmd: string, value?: string) => {
  try { return document.execCommand(cmd, false, value); } catch { return false; }
};
const queryState = (cmd: string) => {
  try { return document.queryCommandState(cmd); } catch { return false; }
};
const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
/** the nearest element with this tag from a node up to (not including) the editor. */
function closest(node: Node | null | undefined, tag: string, root: HTMLElement): HTMLElement | null {
  for (let n: Node | null | undefined = node; n && n !== root; n = n.parentNode) {
    if (n.nodeType === 1 && (n as HTMLElement).tagName === tag) return n as HTMLElement;
  }
  return null;
}
function caretTo(node: Node, atEnd = true) {
  const sel = document.getSelection();
  if (!sel) return;
  const r = document.createRange();
  r.selectNodeContents(node);
  r.collapse(!atEnd);
  sel.removeAllRanges();
  sel.addRange(r);
}
const LINE_BLOCKS = new Set(['DIV', 'P', 'LI', 'BLOCKQUOTE', 'PRE', 'UL', 'OL']);
const blank = (s: string | null | undefined) => !(s ?? '').replace(/[\u200b\s]/g, '');
/** the caret sits on an empty line at the end of `block`: nothing typed after it, and nothing before it on
 *  its own line (a line break, or the start of a line, comes first). */
function onEmptyLastLine(block: HTMLElement): boolean {
  const sel = document.getSelection();
  if (!sel?.rangeCount || !sel.isCollapsed) return false;
  const caret = sel.getRangeAt(0);
  const after = document.createRange();
  after.setStart(caret.endContainer, caret.endOffset);
  after.setEnd(block, block.childNodes.length);
  if (!blank(after.toString())) return false;
  const before = document.createRange();
  before.setStart(block, 0);
  before.setEnd(caret.startContainer, caret.startOffset);
  // what's before the caret, from the nearest thing back: text (the line has some), a line break, or a block
  // (the caret's own line, cut off where the caret is, or the line before it) ends the search
  const walk = document.createTreeWalker(before.cloneContents(), NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  let n: Node | null = walk.currentNode;
  while (walk.lastChild()) n = walk.currentNode;
  for (; n && n !== walk.root; n = walk.previousNode()) {
    if (n.nodeType === Node.TEXT_NODE) {
      if (!blank(n.nodeValue)) return false;
    } else if (n.nodeName === 'BR') return true;
    else if (LINE_BLOCKS.has(n.nodeName)) return blank(n.textContent);
  }
  return true; // nothing before it at all
}
const MOD = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl+';
/** a press on a toolbar button keeps the focus (and the selection) in the message */
const keep = { onPointerDown: (e: { preventDefault: () => void }) => e.preventDefault(), onMouseDown: (e: { preventDefault: () => void }) => e.preventDefault() };

export interface ComposerProps {
  thread: string;
  uploads: ChatUploads;
  /** one of my messages being edited (its markup, and whether it has files, so the text may go) */
  editing: { id: number; html: string; hasFiles: boolean } | null;
  onCancelEdit: () => void;
  /** send; resolves true once sent (the composer then empties) */
  onSend: (html: string) => Promise<boolean>;
  onSave: (id: number, html: string) => Promise<boolean>;
  /** Up in an empty composer: edit my last message */
  onEditLast: () => void;
  sending: boolean;
  /** set to a function that puts the focus back in the message (not on a touch screen, where that would
   *  pop the keyboard up): for when a button that had it goes away (a reaction picked, a message deleted) */
  focusRef?: MutableRefObject<(() => void) | null>;
}

type Pop = { kind: 'link'; href: string; needsText: boolean } | { kind: 'emoji'; focusFirst: boolean } | null;

export function ChatComposer({ thread, uploads, editing, onCancelEdit, onSend, onSave, onEditLast, sending, focusRef }: ComposerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const saved = useRef<Range | null>(null);
  const stash = useRef<string | null>(null);
  const phone = useIsPhone();
  const coarse = useIsCoarse();
  const [empty, setEmpty] = useState(true);
  const [hasText, setHasText] = useState(false);
  const [tools, setTools] = useState(false);
  const [active, setActive] = useState('');
  const [pop, setPop] = useState<Pop>(null);
  const editingId = editing?.id ?? null;

  const markup = () => trimChatHtml(sanitizeChatHtml(ref.current?.innerHTML ?? ''));
  // after every change: the placeholder, whether there's anything to send, and the thread's draft
  const changed = () => {
    const el = ref.current;
    if (!el) return;
    setEmpty(!(el.textContent ?? '').replace(/[\u200b\s]/g, '') && !el.querySelector('li,blockquote'));
    const text = chatHtmlToText(markup());
    setHasText(!!text);
    if (stash.current == null) {
      if (text) drafts.set(thread, el.innerHTML);
      else drafts.delete(thread);
    }
  };

  // the thread's draft, and the focus on a desktop (a phone would pop its keyboard up)
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    exec('styleWithCSS', 'false');
    const draft = drafts.get(thread);
    if (draft) el.innerHTML = sanitizeChatHtml(draft);
    changed();
    if (!coarse) { el.focus({ preventScroll: true }); caretTo(el); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // editing one of my messages: put the draft aside and load the message; done, bring the draft back
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (editing) {
      if (stash.current == null) stash.current = el.innerHTML;
      el.innerHTML = sanitizeChatHtml(editing.html);
      el.focus({ preventScroll: true });
      caretTo(el);
    } else if (stash.current != null) {
      el.innerHTML = stash.current;
      stash.current = null;
    }
    setPop(null);
    changed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId]);

  // which formats are on where the caret is (the toolbar shows them pressed); the selection is kept for
  // the link box and the emoji grid, which take the focus away
  useEffect(() => {
    const onSel = () => {
      const el = ref.current;
      const sel = document.getSelection();
      if (!el || !sel?.rangeCount || !el.contains(sel.anchorNode)) return;
      saved.current = sel.getRangeAt(0).cloneRange();
      const at = sel.anchorNode;
      const on: string[] = [];
      if (queryState('bold')) on.push('bold');
      if (queryState('italic')) on.push('italic');
      if (queryState('underline')) on.push('underline');
      if (queryState('strikeThrough')) on.push('strike');
      if (closest(at, 'UL', el)) on.push('ul');
      if (closest(at, 'OL', el)) on.push('ol');
      if (closest(at, 'BLOCKQUOTE', el)) on.push('quote');
      if (closest(at, 'CODE', el)) on.push('code');
      if (closest(at, 'A', el)) on.push('link');
      const key = on.join(' ');
      setActive((p) => (p === key ? p : key));
    };
    document.addEventListener('selectionchange', onSel);
    return () => document.removeEventListener('selectionchange', onSel);
  }, []);
  const isOn = (c: string) => active.split(' ').includes(c);

  /** the focus back in the message, with the selection it had (left alone while it's still there: the saved
   *  one trails fast typing, as selectionchange arrives late) */
  const refocus = () => {
    const el = ref.current!;
    const sel = document.getSelection();
    if (document.activeElement === el && sel?.rangeCount && el.contains(sel.anchorNode)) return;
    el.focus({ preventScroll: true });
    if (!sel) return;
    if (saved.current && el.contains(saved.current.startContainer)) {
      sel.removeAllRanges();
      sel.addRange(saved.current);
    } else if (!el.contains(sel.anchorNode)) {
      caretTo(el);
    }
  };

  useEffect(() => {
    if (!focusRef) return;
    focusRef.current = () => { if (!coarse && ref.current) refocus(); };
    return () => { focusRef.current = null; };
  });

  const toggleCode = () => {
    const el = ref.current!;
    const sel = document.getSelection();
    if (!sel?.rangeCount) return;
    const range = sel.getRangeAt(0);
    const code = closest(range.commonAncestorContainer, 'CODE', el);
    if (code && range.collapsed) {
      // step out of the code: what's typed next is plain again
      const after = document.createTextNode('\u200b');
      code.after(after);
      caretTo(after);
    } else if (code) {
      const text = document.createTextNode(code.textContent ?? '');
      code.replaceWith(text);
      caretTo(text);
    } else if (!range.collapsed) {
      exec('insertHTML', `<code>${esc(range.toString())}</code>`);
    } else {
      const fresh = document.createElement('code');
      fresh.textContent = '\u200b';
      range.insertNode(fresh);
      caretTo(fresh);
    }
  };
  const toggleQuote = () => {
    const el = ref.current!;
    const quote = closest(document.getSelection()?.anchorNode, 'BLOCKQUOTE', el);
    if (!quote) { exec('formatBlock', 'blockquote'); return; }
    const div = document.createElement('div');
    while (quote.firstChild) div.appendChild(quote.firstChild);
    quote.replaceWith(div);
    caretTo(div);
  };

  const openLink = () => {
    refocus();
    const el = ref.current!;
    const sel = document.getSelection();
    saved.current = sel?.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
    const a = closest(sel?.anchorNode, 'A', el);
    setPop({ kind: 'link', href: a?.getAttribute('href') ?? '', needsText: !a && !!sel?.isCollapsed });
  };
  const applyLink = (href: string, text: string) => {
    setPop(null);
    refocus();
    const el = ref.current!;
    const sel = document.getSelection()!;
    const a = closest(sel.anchorNode, 'A', el);
    if (a && sel.isCollapsed) a.setAttribute('href', href);
    else if (!sel.isCollapsed) {
      exec('createLink', href);
      if (sel.rangeCount) sel.collapseToEnd(); // what's typed next goes after the link, not over it
    } else exec('insertHTML', `<a href="${esc(href)}">${esc(text || href)}</a>`);
    changed();
  };
  const removeLink = () => {
    setPop(null);
    refocus();
    const el = ref.current!;
    const sel = document.getSelection()!;
    const a = closest(sel.anchorNode, 'A', el);
    if (a) {
      const r = document.createRange();
      r.selectNodeContents(a);
      sel.removeAllRanges();
      sel.addRange(r);
    }
    exec('unlink');
    changed();
  };

  const run = (c: FormatCommand) => {
    if (c === 'link') { openLink(); return; }
    refocus();
    if (c === 'bold') exec('bold');
    else if (c === 'italic') exec('italic');
    else if (c === 'underline') exec('underline');
    else if (c === 'strike') exec('strikeThrough');
    else if (c === 'ul') exec('insertUnorderedList');
    else if (c === 'ol') exec('insertOrderedList');
    else if (c === 'quote') toggleQuote();
    else if (c === 'code') toggleCode();
    changed();
  };
  const clearFormatting = () => {
    refocus();
    exec('removeFormat');
    exec('unlink');
    changed();
  };
  const pickEmoji = (emoji: string) => {
    rememberEmoji(emoji);
    setPop(null);
    refocus();
    exec('insertText', emoji);
    changed();
  };

  /** out of a quote: its empty last line goes, and the caret moves to a new line below it (an empty quote
   *  goes altogether) */
  const leaveQuote = (quote: HTMLElement) => {
    for (let last = quote.lastChild; last; last = quote.lastChild) {
      const empty = last.nodeName === 'BR' || (last.nodeType === Node.TEXT_NODE && blank(last.nodeValue)) || (LINE_BLOCKS.has(last.nodeName) && blank(last.textContent));
      if (!empty) break;
      last.remove();
    }
    const line = document.createElement('div');
    line.appendChild(document.createElement('br'));
    quote.after(line);
    if (!quote.firstChild) quote.remove();
    caretTo(line, false);
    changed();
  };

  const blocked = sending || uploads.busy || uploads.failed;
  const canSend = !blocked && (editing ? hasText || editing.hasFiles : hasText || uploads.readyIds.length > 0);
  const submit = async () => {
    if (!canSend) return;
    const html = markup();
    const text = chatHtmlToText(html);
    if (editing) {
      await onSave(editing.id, text ? html : '');
      return;
    }
    // the box empties as the message goes, so whatever is typed meanwhile starts the next one; a message
    // that doesn't go comes back (with anything typed since on a line after it)
    const el = ref.current;
    if (!el) return;
    const typed = el.innerHTML;
    el.innerHTML = '';
    // the browser keeps typing in bold (or italic …) in the emptied box; the next message starts plain
    if (document.activeElement === el) {
      for (const c of ['bold', 'italic', 'underline', 'strikeThrough']) if (queryState(c)) exec(c);
    }
    drafts.delete(thread);
    setActive('');
    changed();
    if (await onSend(text ? html : '')) return;
    const since = el.innerHTML;
    el.innerHTML = typed + (chatHtmlToText(sanitizeChatHtml(since)) ? `<div>${since}</div>` : '');
    if (!el.isConnected) { drafts.set(thread, el.innerHTML); return; } // another thread is open by now
    if (document.activeElement === el) caretTo(el);
    changed();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.nativeEvent.isComposing || e.keyCode === 229) return; // an IME is composing a character
    const cmd = shortcutFor(e);
    if (cmd) { e.preventDefault(); run(cmd); return; }
    if (e.key === 'Enter') {
      if (e.ctrlKey || e.metaKey || (!e.shiftKey && !coarse)) { e.preventDefault(); void submit(); return; }
      // Shift+Enter in a list starts the next item; on an empty item it ends the list
      const at = document.getSelection()?.anchorNode;
      const li = closest(at, 'LI', ref.current!);
      if (li) {
        e.preventDefault();
        if ((li.textContent ?? '').replace(/\u200b/g, '').trim()) exec('insertParagraph');
        else exec(closest(li, 'OL', ref.current!) ? 'insertOrderedList' : 'insertUnorderedList');
        changed();
        return;
      }
      // in a quote, a new line stays in it (a touch keyboard's Enter would start a second quote); on the
      // quote's empty last line it leaves the quote instead
      const quote = closest(at, 'BLOCKQUOTE', ref.current!);
      if (quote) {
        e.preventDefault();
        if (onEmptyLastLine(quote)) leaveQuote(quote);
        else { exec('insertLineBreak'); changed(); }
      }
      return;
    }
    if (e.key === 'ArrowUp' && !editing && empty && !uploads.items.length) { e.preventDefault(); onEditLast(); }
  };
  // Escape closes the link box or the emoji grid first, then leaves editing; otherwise it closes the panel
  const onRootKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape') return;
    if (pop) { e.preventDefault(); e.stopPropagation(); setPop(null); refocus(); }
    else if (editing) { e.preventDefault(); e.stopPropagation(); onCancelEdit(); }
  };

  const onPaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const cd = e.clipboardData;
    const files = filesOf(cd);
    const text = cd.getData('text/plain');
    const html = cd.getData('text/html');
    e.preventDefault();
    // an image on its own (a screenshot, an image copied from a page) is a file to send; text copied from
    // Word comes with a picture of itself too, and stays text
    if (files.length && !text.trim()) {
      if (!editing) uploads.add(files);
      return;
    }
    if (html.trim()) exec('insertHTML', trimChatHtml(sanitizeChatHtml(pastedStylesToTags(html))) || esc(text));
    else if (text) exec('insertText', text);
    changed();
  };

  const tb = (c: FormatCommand, label: ReactNode, title: string) => (
    <button type="button" className={`chat-tbtn${isOn(c) ? ' on' : ''}`} title={title} aria-label={title} aria-pressed={isOn(c)} {...keep} onClick={() => run(c)}>
      {label}
    </button>
  );
  const failed = uploads.items.filter((u) => u.status === 'error');

  return (
    <div className={`chat-composer${editing ? ' is-editing' : ''}`} onKeyDown={onRootKeyDown}>
      {editing && (
        <div className="chat-editbar">
          <IconPencil size={13} />
          <span>Editing your message</span>
          <button type="button" className="chat-editbar-x" onClick={onCancelEdit}>Cancel</button>
        </div>
      )}
      {uploads.items.length > 0 && (
        <div className="chat-chips" aria-label="Files to send">
          {uploads.items.map((u) => <Chip key={u.key} u={u} onRemove={uploads.remove} onRetry={uploads.retry} />)}
        </div>
      )}
      {(failed.length > 0 || uploads.notice) && (
        <div className="chat-up-msg" role="alert">
          {failed.map((u) => <div key={u.key}>{`“${u.name}”: ${u.error ?? 'The upload failed.'}`}</div>)}
          {uploads.notice && <div>{uploads.notice}</div>}
          {uploads.notice && <button type="button" className="chat-up-x" aria-label="Dismiss" onClick={uploads.clearNotice}><IconClose size={12} /></button>}
        </div>
      )}
      {(!phone || tools) && (
        <div className="chat-tb" role="toolbar" aria-label="Formatting">
          {tb('bold', <b>B</b>, `Bold (${MOD}B)`)}
          {tb('italic', <i>I</i>, `Italic (${MOD}I)`)}
          {tb('underline', <u>U</u>, `Underline (${MOD}U)`)}
          {tb('strike', <s>S</s>, `Strikethrough (${MOD}Shift+X)`)}
          <span className="chat-tb-sep" aria-hidden="true" />
          {tb('ul', <IconList size={16} />, `Bulleted list (${MOD}Shift+8)`)}
          {tb('ol', <IconListOrdered size={16} />, `Numbered list (${MOD}Shift+7)`)}
          {tb('quote', <IconQuote size={14} />, `Quote (${MOD}Shift+9)`)}
          <span className="chat-tb-sep" aria-hidden="true" />
          {tb('link', <IconLink size={15} />, `Link (${MOD}K)`)}
          {tb('code', <IconCode size={16} />, `Code (${MOD}E)`)}
          <button type="button" className="chat-tbtn" title="Clear formatting" aria-label="Clear formatting" {...keep} onClick={clearFormatting}>
            <IconClearFormat size={15} />
          </button>
          <button
            type="button"
            className={`chat-tbtn chat-tb-emoji${pop?.kind === 'emoji' ? ' on' : ''}`}
            title="Emoji"
            aria-label="Emoji"
            aria-expanded={pop?.kind === 'emoji'}
            data-emoji-toggle=""
            {...keep}
            onClick={(e) => setPop((p) => (p?.kind === 'emoji' ? null : { kind: 'emoji', focusFirst: e.detail === 0 }))}
          >
            <IconSmile size={16} />
          </button>
        </div>
      )}
      <div className="chat-inrow">
        {!editing && (
          <button type="button" className="chat-tool" title="Attach files" aria-label="Attach files" onClick={() => fileRef.current?.click()}>
            <IconPaperclip size={18} />
          </button>
        )}
        {phone && (
          <button type="button" className={`chat-tool chat-aa${tools ? ' on' : ''}`} aria-label="Formatting" aria-pressed={tools} {...keep} onClick={() => setTools((t) => !t)}>
            Aa
          </button>
        )}
        <div
          ref={ref}
          className={`chat-editor${empty ? ' is-empty' : ''}`}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label={editing ? 'Edit message' : 'Message'}
          data-ph={editing ? 'Edit your message…' : 'Message…'}
          enterKeyHint={coarse ? 'enter' : 'send'}
          spellCheck
          onInput={changed}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        />
        <button
          type="button"
          className="chat-send"
          disabled={!canSend}
          aria-label={editing ? 'Save' : 'Send'}
          title={uploads.busy ? 'Waiting for the files to upload' : editing ? 'Save (Enter)' : coarse ? 'Send' : 'Send (Enter)'}
          {...keep}
          onClick={() => void submit()}
        >
          {editing ? <IconCheck size={18} /> : <IconSend size={17} />}
        </button>
      </div>
      <input
        ref={fileRef}
        type="file"
        multiple
        hidden
        tabIndex={-1}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (files.length) uploads.add(files);
        }}
      />
      {pop?.kind === 'link' && (
        <LinkBox
          href={pop.href}
          needsText={pop.needsText}
          onApply={applyLink}
          onRemove={pop.href ? removeLink : undefined}
          onClose={() => { setPop(null); refocus(); }}
        />
      )}
      {pop?.kind === 'emoji' && (
        // closed from its own keys (Escape), the focus comes back to the message; a press elsewhere keeps it there
        <EmojiPicker focusFirst={pop.focusFirst} onPick={pickEmoji} onClose={(byKey) => { setPop(null); if (byKey) refocus(); }} />
      )}
    </div>
  );
}

/** a file waiting in the composer: a thumbnail (or its type and name), the upload's progress, a remove x. */
function Chip({ u, onRemove, onRetry }: { u: PendingUpload; onRemove: (key: number) => void; onRetry: (key: number) => void }) {
  const busy = u.status === 'queued' || u.status === 'preparing' || u.status === 'uploading';
  const badge = fileBadge({ name: u.name, type: u.kind === 'pdf' ? 'application/pdf' : '' });
  const pct = u.status === 'uploading' ? Math.max(4, Math.round(u.progress * 100)) : u.status === 'preparing' ? 4 : 0;
  return (
    <div className={`chat-chip is-${u.status}${u.preview ? ' has-thumb' : ''}`} title={u.error ? `${u.name}: ${u.error}` : `${u.name} · ${formatFileSize(u.size)}`}>
      {u.preview ? <img className="chat-chip-thumb" src={u.preview} alt="" /> : <span className={`chat-chip-ic t-${badge.tone}`}>{badge.label}</span>}
      {!u.preview && (
        <span className="chat-chip-txt">
          <span className="chat-chip-name">{u.name}</span>
          {u.status === 'error' ? (
            // Retry where the size was, so it hides neither the name nor the badge
            <span className="chat-chip-size">Failed · <button type="button" className="chat-chip-retry" aria-label={`Retry ${u.name}`} onClick={() => onRetry(u.key)}>Retry</button></span>
          ) : (
            <span className="chat-chip-size">{formatFileSize(u.size)}</span>
          )}
        </span>
      )}
      {busy && (
        <span className="chat-chip-bar" role="progressbar" aria-label={`Uploading ${u.name}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
          <i style={{ width: `${pct}%` }} />
        </span>
      )}
      {u.status === 'error' && u.preview && (
        <button type="button" className="chat-chip-retry over" aria-label={`Retry ${u.name}`} onClick={() => onRetry(u.key)}>Retry</button>
      )}
      <button type="button" className="chat-chip-x" aria-label={`Remove ${u.name}`} title="Remove" onClick={() => onRemove(u.key)}>
        <IconClose size={11} />
      </button>
    </div>
  );
}

/** the small box for a link: the address (a bare "technohull.com" or an email address is fine), and the
 *  text to show when nothing was selected. */
function LinkBox({ href, needsText, onApply, onRemove, onClose }: {
  href: string;
  needsText: boolean;
  onApply: (href: string, text: string) => void;
  onRemove?: () => void;
  onClose: () => void;
}) {
  const [url, setUrl] = useState(href);
  const [text, setText] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const box = useRef<HTMLFormElement>(null);
  useEffect(() => {
    const down = (e: PointerEvent) => { if (box.current && !box.current.contains(e.target as Node)) onClose(); };
    document.addEventListener('pointerdown', down, true);
    return () => document.removeEventListener('pointerdown', down, true);
  }, [onClose]);
  const apply = () => {
    const ok = normalizeLinkInput(url);
    if (!ok) { setErr('Enter a web address (or an email address).'); return; }
    onApply(ok, text.trim());
  };
  return (
    <form ref={box} className="chat-pop chat-linkbox" aria-label="Link" onSubmit={(e) => { e.preventDefault(); apply(); }}>
      <label className="chat-linkbox-row">
        <span>Link</span>
        <input className="field" autoFocus value={url} onChange={(e) => { setUrl(e.target.value); setErr(null); }} placeholder="https://… or name@technohull.com" inputMode="url" autoCapitalize="off" autoCorrect="off" spellCheck={false} />
      </label>
      {needsText && (
        <label className="chat-linkbox-row">
          <span>Text</span>
          <input className="field" value={text} onChange={(e) => setText(e.target.value)} placeholder="What to show (optional)" />
        </label>
      )}
      {err && <div className="chat-linkbox-err" role="alert">{err}</div>}
      <div className="chat-linkbox-actions">
        {onRemove && <button type="button" className="btn btn-sm btn-ghost" onClick={onRemove}>Remove link</button>}
        <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}>Cancel</button>
        <button type="submit" className="btn btn-sm btn-primary">{href ? 'Update' : 'Add link'}</button>
      </div>
    </form>
  );
}
